import qs from "querystring"
import {flatten} from "q-flat"
import {deleteVerb, get, getResponse, patch, post, put, rawPost} from "../utilities/http"
import {Org} from "./org"
import {Bank} from "./bank"
import {Application, ApplicationDocument} from "./application"
import {Account} from "./account"
import {Customer} from "./customer"
import {ResponseType} from "axios"
import {BeneficialOwnerResource} from "./beneficialOwner"
import {Partner} from "./partner"
import {Payment} from "./payment"
import {User} from "./user"
import {IncomingAch} from "./incomingAch"
import {FileMetadata} from "../utilities/file"
import {CallbackAssignment} from "./assignments"
import {WireDrawdown} from "./wireDrawdown"
import {CheckDeposit} from "./checkDeposits"

export const apiUrl = process.env.API_URL

export interface Error {
	title: string
	status: string
	source?: {
		pointer: string
	}
	detail?: string
	code?: string
}

export interface ErrorDocument {
	errors: Array<Error>
}

export function isErrorDocument(err: any): err is ErrorDocument {
	return Array.isArray(err?.errors)
}

export type Permissible<T> = T | undefined

export type Resource =
	| Org
	| Bank
	| Partner
	| ApplicationDocument
	| Account
	| Customer
	| BeneficialOwnerResource
	| Payment
	| User
	| IncomingAch
	| Application
	| CallbackAssignment
	| WireDrawdown
	| CheckDeposit

export interface OkDocument<T> {
	data: T
	included?: Resource[]
	meta?: Meta
}

export async function createResource<T>(
	path: string,
	accessToken: string,
	data: any,
	additionalHeaders?: Record<string, string>
) {
	const authorizationHeader = accessToken ? {Authorization: `Bearer ${accessToken}`} : {}
	const headers = {
		"Content-Type": "application/vnd.api+json",
		...authorizationHeader,
		...additionalHeaders,
	}

	const url = `${apiUrl}/${path}`

	return await post<OkDocument<T>, ErrorDocument>(url, {data}, {headers})
}

export async function getResource<T>(path: string, accessToken: string, query?: any) {
	const authorizationHeader = accessToken ? {Authorization: `Bearer ${accessToken}`} : {}
	const headers = {
		"Content-Type": "application/vnd.api+json",
		...authorizationHeader,
	}

	const url = query ? `${apiUrl}/${path}?${qs.stringify(flatten(query))}` : `${apiUrl}/${path}`
	return await get<OkDocument<T>, ErrorDocument>(url, {headers})
}

export async function getResourceAsArrayBuffer(path: string, accessToken: string, query?: any) {
	const authorizationHeader = accessToken ? {Authorization: `Bearer ${accessToken}`} : {}
	const headers = {
		"Content-Type": "application/vnd.api+json",
		...authorizationHeader,
	}

	const url = query ? `${apiUrl}/${path}?${qs.stringify(flatten(query))}` : `${apiUrl}/${path}`

	return await get<ArrayBuffer, any>(url, {headers, responseType: "arraybuffer"})
}

export async function updateResource<T>(path: string, accessToken: string, data: any) {
	const authorizationHeader = accessToken ? {Authorization: `Bearer ${accessToken}`} : {}
	const headers = {
		"Content-Type": "application/vnd.api+json",
		...authorizationHeader,
	}

	const url = `${apiUrl}/${path}`

	return await patch<OkDocument<T>, ErrorDocument>(url, {data}, {headers})
}

export async function deleteResource<T>(path: string, accessToken: string) {
	const authorizationHeader = accessToken ? {Authorization: `Bearer ${accessToken}`} : {}
	const headers = {
		"Content-Type": "application/vnd.api+json",
		...authorizationHeader,
	}

	const url = `${apiUrl}/${path}`
	return await deleteVerb<OkDocument<T>, ErrorDocument>(url, {headers})
}

export async function uploadResource<T>({
	path,
	accessToken,
	data,
	type,
	metadata,
}: {
	path: string
	accessToken: string
	data: any
	type?: string
	metadata?: FileMetadata
}) {
	const metadataHeaders = metadata ? {"Content-Disposition": `attachment; filename="${metadata.name}"`} : {}
	const authorizationHeader = accessToken ? {Authorization: `Bearer ${accessToken}`} : {}
	const headers = {
		"Content-Type": type ? type : "application/vnd.api+json",
		...authorizationHeader,
		...metadataHeaders,
	}

	const url = `${apiUrl}/${path}`

	return await put<OkDocument<T>, ErrorDocument>(url, data, {headers})
}

export async function getFile(
	path: string,
	accessToken: string,
	query?: any,
	fileType = "text/csv",
	responseType: ResponseType = "text",
	withFullRespones = false
) {
	const authorizationHeader = accessToken ? {Authorization: `Bearer ${accessToken}`} : {}
	const headers = {
		Accept: fileType,
		...authorizationHeader,
	}

	const url = query ? `${apiUrl}/${path}?${qs.stringify(flatten(query))}` : `${apiUrl}/${path}`
	if (withFullRespones) {
		return await getResponse<ErrorDocument>(url, {headers, responseType})
	}
	return await get<any, ErrorDocument>(url, {headers, responseType})
}

export async function createResourceFromFile<T>(
	path: string,
	accessToken: string,
	data: any,
	type: string,
	fileName: string
) {
	const authorizationHeader = accessToken ? {Authorization: `Bearer ${accessToken}`} : {}
	const headers = {
		"Content-Type": type,
		"file-name": fileName,
		...authorizationHeader,
	}
	const url = `${apiUrl}/${path}`
	return await post<OkDocument<T>, ErrorDocument>(url, data, {headers})
}

export async function rawCreateResource<T>(path: string, accessToken: string, data: any, extraHeaders: any) {
	const authorizationHeader = accessToken ? {Authorization: `Bearer ${accessToken}`} : {}
	const headers = {
		"Content-Type": "application/vnd.api+json",
		...authorizationHeader,
	}

	const url = `${apiUrl}/${path}`

	return await rawPost<OkDocument<T>, ErrorDocument>(url, data, {headers: Object.assign(extraHeaders, headers)})
}

export type FullName = {
	first: string
	last: string
}

export type Address = {
	street: string
	street2?: string
	city: string
	state?: string
	postalCode: string
	country: string
}

export type Phone = {
	countryCode: string
	number: string
}

export function toPhoneNumber(phone: Phone) {
	return ["+", phone.countryCode, phone.number].join("")
}

export type BusinessContact = {
	fullName: FullName
	email: Permissible<string>
	phone: Permissible<Phone>
	title?: string
}

export type AuthorizedUser = {
	fullName: FullName
	email: string
	phone: Phone
}

export enum RiskRate {
	Low = "low",
	Medium = "medium",
	High = "high",
}

export enum EditMode {
	NEW = "NEW",
	EDIT = "EDIT",
	VIEW = "VIEW",
}

export type IpLocationDetails = {
	city: string
	stateCode: string
	countryCode: string
}

export type PhoneLocationDetails = {
	city: string
	stateCode: string
	countryCode: string
}

export type Pagination = {
	offset: string
	limit: string
	total: string
}

export type Meta = {
	pagination: Pagination
}

export type EvaluationScores = {
	Socure30: number
	IDAnalyticsIDScore: number
	Iovation: number
}

export const hiddenValue = "●●●"

export function isMasked(value: string) {
	return value.includes("*")
}

export enum Countries {
	AF = "Afghanistan",
	AL = "Albania",
	DZ = "Algeria",
	AS = "American Samoa",
	AD = "Andorra",
	AO = "Angola",
	AI = "Anguilla",
	AQ = "Antarctica",
	AG = "Antigua and Barbuda",
	AR = "Argentina",
	AM = "Armenia",
	AW = "Aruba",
	AU = "Australia",
	AT = "Austria",
	AZ = "Azerbaijan",
	BS = "Bahamas",
	BH = "Bahrain",
	BD = "Bangladesh",
	BB = "Barbados",
	BY = "Belarus",
	BE = "Belgium",
	BZ = "Belize",
	BJ = "Benin",
	BM = "Bermuda",
	BT = "Bhutan",
	BO = "Bolivia",
	BA = "Bosnia and Herzegovina",
	BW = "Botswana",
	BV = "Bouvet Island",
	BR = "Brazil",
	IO = "British Indian Ocean Territory",
	BN = "Brunei Darussalam",
	BG = "Bulgaria",
	BF = "Burkina Faso",
	BI = "Burundi",
	KH = "Cambodia",
	CM = "Cameroon",
	CA = "Canada",
	CV = "Cape Verde",
	KY = "Cayman Islands",
	CF = "Central African Republic",
	TD = "Chad",
	CL = "Chile",
	CN = "China",
	CX = "Christmas Island",
	CC = "Cocos (Keeling) Islands",
	CO = "Colombia",
	KM = "Comoros",
	CG = "Congo",
	CD = "Congo, the Democratic Republic of the",
	CK = "Cook Islands",
	CR = "Costa Rica",
	CI = "Cote D'Ivoire",
	HR = "Croatia",
	CU = "Cuba",
	CY = "Cyprus",
	CZ = "Czech Republic",
	DK = "Denmark",
	DJ = "Djibouti",
	DM = "Dominica",
	DO = "Dominican Republic",
	EC = "Ecuador",
	EG = "Egypt",
	SV = "El Salvador",
	GQ = "Equatorial Guinea",
	ER = "Eritrea",
	EE = "Estonia",
	ET = "Ethiopia",
	FK = "Falkland Islands (Malvinas)",
	FO = "Faroe Islands",
	FJ = "Fiji",
	FI = "Finland",
	FR = "France",
	GF = "French Guiana",
	PF = "French Polynesia",
	TF = "French Southern Territories",
	GA = "Gabon",
	GM = "Gambia",
	GE = "Georgia",
	DE = "Germany",
	GH = "Ghana",
	GI = "Gibraltar",
	GR = "Greece",
	GL = "Greenland",
	GD = "Grenada",
	GP = "Guadeloupe",
	GU = "Guam",
	GT = "Guatemala",
	GN = "Guinea",
	GW = "Guinea-Bissau",
	GY = "Guyana",
	HT = "Haiti",
	HM = "Heard Island and McDonald Islands",
	VA = "Holy See (Vatican City State)",
	HN = "Honduras",
	HK = "Hong Kong",
	HU = "Hungary",
	IS = "Iceland",
	IN = "India",
	ID = "Indonesia",
	IR = "Iran, Islamic Republic of",
	IQ = "Iraq",
	IE = "Ireland",
	IL = "Israel",
	IT = "Italy",
	JM = "Jamaica",
	JP = "Japan",
	JO = "Jordan",
	KZ = "Kazakhstan",
	KE = "Kenya",
	KI = "Kiribati",
	KP = "North Korea",
	KR = "South Korea",
	KW = "Kuwait",
	KG = "Kyrgyzstan",
	LA = "Lao People's Democratic Republic",
	LV = "Latvia",
	LB = "Lebanon",
	LS = "Lesotho",
	LR = "Liberia",
	LY = "Libya",
	LI = "Liechtenstein",
	LT = "Lithuania",
	LU = "Luxembourg",
	MO = "Macao",
	MG = "Madagascar",
	MW = "Malawi",
	MY = "Malaysia",
	MV = "Maldives",
	ML = "Mali",
	MT = "Malta",
	MH = "Marshall Islands",
	MQ = "Martinique",
	MR = "Mauritania",
	MU = "Mauritius",
	YT = "Mayotte",
	MX = "Mexico",
	FM = "Micronesia, Federated States of",
	MD = "Moldova, Republic of",
	MC = "Monaco",
	MN = "Mongolia",
	MS = "Montserrat",
	MA = "Morocco",
	MZ = "Mozambique",
	MM = "Myanmar",
	NA = "Namibia",
	NR = "Nauru",
	NP = "Nepal",
	NL = "Netherlands",
	NC = "New Caledonia",
	NZ = "New Zealand",
	NI = "Nicaragua",
	NE = "Niger",
	NG = "Nigeria",
	NU = "Niue",
	NF = "Norfolk Island",
	MK = "North Macedonia, Republic of",
	MP = "Northern Mariana Islands",
	NO = "Norway",
	OM = "Oman",
	PK = "Pakistan",
	PW = "Palau",
	PS = "State of Palestine",
	PA = "Panama",
	PG = "Papua New Guinea",
	PY = "Paraguay",
	PE = "Peru",
	PH = "Philippines",
	PN = "Pitcairn",
	PL = "Poland",
	PT = "Portugal",
	PR = "Puerto Rico",
	QA = "Qatar",
	RE = "Reunion",
	RO = "Romania",
	RU = "Russian Federation",
	RW = "Rwanda",
	SH = "Saint Helena",
	KN = "Saint Kitts and Nevis",
	LC = "Saint Lucia",
	PM = "Saint Pierre and Miquelon",
	VC = "Saint Vincent and the Grenadines",
	WS = "Samoa",
	SM = "San Marino",
	ST = "Sao Tome and Principe",
	SA = "Saudi Arabia",
	SN = "Senegal",
	SC = "Seychelles",
	SL = "Sierra Leone",
	SG = "Singapore",
	SK = "Slovakia",
	SI = "Slovenia",
	SB = "Solomon Islands",
	SO = "Somalia",
	ZA = "South Africa",
	GS = "South Georgia and the South Sandwich Islands",
	ES = "Spain",
	LK = "Sri Lanka",
	SD = "Sudan",
	SR = "Suriname",
	SJ = "Svalbard and Jan Mayen",
	SZ = "Eswatini",
	SE = "Sweden",
	CH = "Switzerland",
	SY = "Syrian Arab Republic",
	TW = "Taiwan",
	TJ = "Tajikistan",
	TZ = "Tanzania, United Republic of",
	TH = "Thailand",
	TL = "Timor-Leste",
	TG = "Togo",
	TK = "Tokelau",
	TO = "Tonga",
	TT = "Trinidad and Tobago",
	TN = "Tunisia",
	TR = "Turkey",
	TM = "Turkmenistan",
	TC = "Turks and Caicos Islands",
	TV = "Tuvalu",
	UG = "Uganda",
	UA = "Ukraine",
	AE = "United Arab Emirates",
	GB = "United Kingdom",
	US = "United States of America",
	UM = "United States Minor Outlying Islands",
	UY = "Uruguay",
	UZ = "Uzbekistan",
	VU = "Vanuatu",
	VE = "Venezuela",
	VN = "Vietnam",
	VG = "Virgin Islands, British",
	VI = "Virgin Islands, U.S.",
	WF = "Wallis and Futuna",
	EH = "Western Sahara",
	YE = "Yemen",
	ZM = "Zambia",
	ZW = "Zimbabwe",
	AX = "Åland Islands",
	BQ = "Bonaire, Sint Eustatius and Saba",
	CW = "Curaçao",
	GG = "Guernsey",
	IM = "Isle of Man",
	JE = "Jersey",
	ME = "Montenegro",
	BL = "Saint Barthélemy",
	MF = "Saint Martin (French part)",
	RS = "Serbia",
	SX = "Sint Maarten (Dutch part)",
	SS = "South Sudan",
	XK = "Kosovo",
}
